import React from "react";

const HumanAndAnimalSubjects = () => {
  return (
    <React.Fragment>
      <h1>Human and Animal Subjects</h1>
      <p>
        Where applicable, CJoE requires that authors provide information detailing the relevant
        ethical standards which were met when conducting research involving the study of human
        and/or animal participants or tissue.
      </p>
      <p>
        Authors are required to provide such information whenever research is conducted on humans or
        human tissue; on animals or animal tissue; when conducting field studies; or in any instance
        requiring the approval of an Institutional Review Board (IRB). In cases that required IRB
        approval, the IRB that granted the approval should be named in the article.
      </p>
      <p>
        This information is included in a disclosure statement attached to the final published
        article.
      </p>

      <h2>
        <strong>Human Subjects Research</strong>
      </h2>
      <p>
        For studies involving human subjects, appropriate approval must have been obtained from the
        relevant institutional review board or ethics committee. Before conducting the research,
        approval must have been obtained from an institutional review board (IRB) or ethics
        committee.{" "}
        <strong>
          The author must also include this information detailing their approval as part of Ethics
          Statements and COI Disclosures &gt; Human Subjects (including the name of the granting
          organization, the approval reference numbers and, optionally, the statement, approval
          letter and/or application approval).
        </strong>
      </p>
      <p>
        Authors must include a statement confirming that informed consent was either obtained or
        waived. Wherever possible, a copy of the consent form should be provided as supplemental
        information.
      </p>
      <p>
        Please send a copy of the aforementioned consent form to{" "}
        <a href="mailto:cureus-support@springernature.com"> cureus-support@springernature.com</a>.
      </p>
      <p>
        If consent was verbal instead of written, then an explanation should be provided (in the
        Materials and Methods section) and the verbal consent must have been approved by the IRB
        which gave permission for the study.
      </p>
      <p>
        Participant privacy and anonymity must be protected. Authors must confirm that no
        identifying information (such as names, images and identifying data) is included in the
        article. Exceptions may be made only if the individuals in question have provided explicit
        approval. For more information about participant privacy, anonymity and informed consent,
        please refer to the{" "}
        <a href="http://www.icmje.org/recommendations" target="_blank" rel="noreferrer noopener">
          ICMJE Privacy and Confidentiality guidelines
        </a>
        .
      </p>
      <h2>
        <strong>Declaration of Helsinki</strong>
      </h2>
      <p>
        Research involving human research participants must have been performed in accordance with
        the Declaration of Helsinki.
      </p>

      <h2>
        <strong>Identifying Information</strong>
      </h2>
      <p>
        Human participants' names and other HIPAA identifiers must be removed from all sections of
        the manuscript, including supplementary information. Written informed consent must be
        obtained for the publication of any other information that could lead to the identification
        of any study participant (e.g., images and videos). A statement confirming that informed
        consent to publish identifying information/images was obtained must be included in the
        disclosures. Identifying images/video/details which authors do not have specific permission
        to use must be removed from the manuscript. Please note that the use of colored bars/shapes
        to obscure the eyes/facial region of study participants is NOT an acceptable means of
        anonymization.
      </p>

      <h2>
        <strong>Studies Involving Vulnerable Groups </strong>
      </h2>
      <p>
        For manuscripts reporting studies involving vulnerable groups where there is the potential
        for coercion or where consent may not have been fully informed, extra care will be taken by
        the editor. The manuscript may be referred to an internal editorial oversight group for
        further scrutiny. Consent must be obtained for all forms of personally identifiable data
        including biomedical, clinical, and biometric data. Documentary evidence of consent must be
        supplied if requested.
      </p>

      <h2>
        <strong>Sex and Gender in Research </strong>
      </h2>
      <p>
        We encourage our authors to follow the "Sex and Gender Equity in Research – SAGER –
        guidelines" and to include sex and gender considerations where relevant. Authors should use
        the terms sex (biological attribute) and gender (shaped by social and cultural
        circumstances) carefully to avoid confusing both terms. These guidelines apply to studies
        involving humans, vertebrate animals, and cell lines.
      </p>

      <h2>
        <strong>Animal Research</strong>
      </h2>
      <p>
        For studies involving regulated animals (which includes all live vertebrates and/or higher
        invertebrates), appropriate approval must have been obtained according to either
        international or local laws and regulations. Before conducting the research, approval must
        have been obtained from an IRB or ethics committee. Authors must also include information
        detailing their approval as part of their research methods section (including the name of
        the granting organization and the protocol number). It should be noted that non-human
        primates research is subject to specific guidelines from the Weatherall (2006) report{" "}
        <a
          href="http://www.easac.eu/fileadmin/PDF_s/reports_statements/Use.pdf"
          target="_blank"
          rel="noreferrer noopener"
        >
          (The Use of Non-Human Primates in Research)
        </a>
        .
      </p>
      <p>
        For research conducted on non-regulated animals, the author must explain why ethical
        approval was not required. The handling of experimental animals should be held to the
        highest standards kept by the author’s institution.
      </p>
      <p>
        CJoE strongly encourages all authors to comply with the “
        <a href="https://arriveguidelines.org/" target="_blank" rel="noopener noreferrer">
          Animal Research: Reporting In Vivo Experiments (ARRIVE)
        </a>
        ” guidelines. Articles should be specific in descriptions of the organism(s) used in the
        study. When known, these descriptions should indicate strain names.
      </p>
    </React.Fragment>
  );
};

export default HumanAndAnimalSubjects;
